<div class="finance-list-page">
    <app-page-shell>
        <p-toast position="top-center"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero eyebrow="Payables" title="Vouchers"
            subtitle="Manage generated and manual vouchers using the current allocation and payment transaction actions.">
            <div actions>
                <p-button *ngIf="canCreateVoucher && !isConsolidatedReadOnly" label="Create Voucher" icon="fa-solid fa-wallet"
                    severity="info" (click)="createVoucher()"></p-button>
                <p-button *ngIf="canApplyVoucher && !isConsolidatedReadOnly" label="Apply Voucher" icon="fa-solid fa-sliders"
                    severity="warn" (click)="applyVoucherOnBill()" [disabled]="!selectedVouchers.length"></p-button>
                <p-button *ngIf="canRegisterPayment && !isConsolidatedReadOnly" label="Register Payment" icon="fa-solid fa-trowel-bricks"
                    severity="help" (click)="generatePaymentFromVouchers()" [disabled]="!selectedVouchers.length"></p-button>
            </div>
        </app-page-hero>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <app-page-search-panel title="Search vouchers" [activeFilters]="activeFilterCount"
            [resultText]="resultText" [collapsed]="true" (search)="applyFilters()" (clear)="clearFilters()">
            <div class="finance-search-grid">
                <app-company-scope-selector (scopeChange)="onScopeChange($event)"></app-company-scope-selector>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="client" [options]="clients" [(ngModel)]="selectedClients"
                                optionLabel="name" display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="client">Vendors</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker inputId="dateRange" [(ngModel)]="dateRange" selectionMode="range"
                                [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body"
                                styleClass="w-full"></p-datePicker>
                            <label for="dateRange">Voucher Date Range</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="status" [options]="statuses" [(ngModel)]="selectedStatuses"
                                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="status">Status</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-receipt"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="voucherNumber" type="text" pInputText [(ngModel)]="voucherNumberFilter" />
                            <label for="voucherNumber">Voucher #</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="paymentTxnNumber" type="text" pInputText
                                [(ngModel)]="paymentTransactionNumberFilter" />
                            <label for="paymentTxnNumber">Payment Transaction #</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-search-panel>

        <app-page-data-panel title="Voucher directory"
            subtitle="Allocation status remains based on linked bills until Phase 2 allocation corrections."
            [resultText]="resultText">
            <div actions>
                <p-button label="Export" icon="pi pi-file-excel" severity="secondary" [outlined]="true"
                    [loading]="isLoading" (click)="exportToExcel()"></p-button>
            </div>

            <div class="bulk-action-bar" *ngIf="selectedVouchers.length && !isConsolidatedReadOnly">
                <div>
                    <strong>{{ selectedVouchers.length }}</strong> selected,
                    <strong>{{ formatMoney(getSelectedTotal()) }}</strong>
                </div>
                <div class="bulk-actions">
                    <p-button *ngIf="canApplyVoucher" label="Apply Voucher" icon="fa-solid fa-sliders"
                        severity="warn" (click)="applyVoucherOnBill()" [disabled]="!selectedVouchers.length"></p-button>
                    <p-button *ngIf="canRegisterPayment" label="Register Payment" icon="fa-solid fa-trowel-bricks"
                        severity="help" (click)="generatePaymentFromVouchers()" [disabled]="!selectedVouchers.length"></p-button>
                </div>
            </div>

            <app-accounting-directory-controls documentType="VOUCHER" [filters]="directoryFilters"
                [searchText]="searchText" [selectedStatuses]="selectedDocumentStatuses" [pendingStatuses]="pendingStatuses" (searchTextChange)="onDirectorySearch($event)"
                (modeChange)="onDirectoryModeChange($event)" (pending)="showPendingStatuses()"
                (all)="showAllStatuses()"></app-accounting-directory-controls>
            <p-table *ngIf="directoryMode === 'LIST'" class="finance-table" [value]="allVouchers" dataKey="voucherId" [paginator]="true"
                [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords"
                [lazy]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                (onLazyLoad)="loadData($event)" [rowsPerPageOptions]="[20, 50, 100]"
                [(selection)]="selectedVouchers" [tableStyle]="{ 'min-width': '0' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th *ngIf="!isConsolidatedReadOnly" style="width: 3.5rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
                        <th>Voucher</th>
                        <th>Vendor</th>
                        <th>Applied/Unapplied Amounts</th>
                        <th>Release/Reconciliation Status</th>
                        <th>Payment Details</th>
                        <th>Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-voucher>
                    <tr>
                        <td *ngIf="!isConsolidatedReadOnly" data-label="Select"><p-tableCheckbox [value]="voucher"></p-tableCheckbox></td>
                        <td data-label="Voucher">
                            <div class="stack-cell">
                                <span class="cell-title">{{ voucher.voucherNumber || 'No voucher number' }}</span>
                                <div class="cell-meta">
                                    <span class="code-pill">ID {{ voucher.voucherId || '-' }}</span>
                                    <span>{{ voucher.voucherDate | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Vendor">
                            <div class="stack-cell">
                                <span class="cell-title">{{ crmPartyContext.displayName(voucher) }}</span>
                                <div class="cell-meta">Contact: {{ crmPartyContext.contactDisplayName(voucher) }}</div>
                            </div>
                        </td>
                        <td data-label="Applied/Unapplied Amounts">
                            <div class="amount-block">
                                <span class="amount-primary">{{ voucher.amount | currency:voucher.country?.currencyCode:(voucher.country?.currencyCode || 'SAR') + ' ' : '1.4-4' }}</span>
                                <span class="amount-secondary">Applied {{ getAppliedAmount(voucher) | currency:voucher.country?.currencyCode:(voucher.country?.currencyCode || 'SAR') + ' ' : '1.4-4' }}</span>
                                <span class="amount-secondary">Unapplied {{ getUnAppliedAmount(voucher) | currency:voucher.country?.currencyCode:(voucher.country?.currencyCode || 'SAR') + ' ' : '1.4-4' }}</span>
                            </div>
                        </td>
                        <td data-label="Release/Reconciliation Status">
                            <div class="status-stack">
                                <p-tag *ngIf="voucher.documentStatus" [value]="voucher.documentStatus" [severity]="statusSeverity(voucher.documentStatus)"></p-tag>
                                <p-tag [severity]="statusSeverity(voucher.status)" [value]="voucher.status || 'UNKNOWN'"></p-tag>
                                <span class="muted-pill">{{ voucher.bills?.length || 0 }} bills</span>
                            </div>
                        </td>
                        <td data-label="Payment Details">
                            <div class="stack-cell">
                                <span class="cell-title">{{ voucher.paymentTransactionNumber || 'No payment transaction' }}</span>
                                <div class="cell-meta vertical">
                                    <span>{{ voucher.isDigitalPayment ? 'Digital payment' : 'Manual payment' }}</span>
                                    <span>Created {{ voucher.createdAt | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Actions">
                            <div class="row-actions">
                                <p-button *ngIf="canEditDraft(voucher)" icon="pi pi-pencil" [rounded]="true" [text]="true" pTooltip="Edit draft" (click)="editDraft(voucher)"></p-button>
                                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" pTooltip="View voucher"
                                    (click)="viewVoucher(voucher)"></p-button>
                                <p-button icon="pi pi-list" [rounded]="true" [text]="true" pTooltip="Preview bills"
                                    (click)="openPreview(voucher)"></p-button>
                                <app-finance-pdf-view-button kind="voucher" [documentId]="voucher.voucherId" [documentReference]="voucher.voucherNumber" [postingStatus]="voucher.postingStatus" [iconOnly]="true" [disabled]="documentActions.isPending(voucher.voucherId)"></app-finance-pdf-view-button>
                                <p-button [icon]="voucher.postingStatus === 'POSTED' ? 'pi pi-download' : 'pi pi-print'" [rounded]="true" [text]="true" severity="info" [loading]="documentActions.isPending(voucher.voucherId)"
                                    [pTooltip]="voucher.postingStatus === 'POSTED' ? 'Download Voucher' : 'Print / reprint draft from latest saved changes'" (click)="downloadVoucher(voucher)"></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7">
                            <div class="empty-state">
                                <i class="pi pi-inbox"></i>
                                <strong>No vouchers match these filters</strong>
                                <span>Clear filters or refresh the list to view generated vouchers.</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </app-page-shell>

    <p-drawer [(visible)]="isPreviewVisible" position="right" styleClass="finance-preview-drawer">
        <ng-template pTemplate="header">
            <div class="stack-cell">
                <strong>{{ previewVoucher?.voucherNumber || 'Linked bills' }}</strong>
                <span class="cell-meta">{{ previewVoucher?.bills?.length || 0 }} bills</span>
            </div>
        </ng-template>
        <div class="preview-list" *ngIf="previewVoucher?.bills?.length; else noVoucherBills">
            <div class="preview-line" *ngFor="let bill of previewVoucher?.bills">
                <div class="preview-line-header">
                    <div>
                        <div class="preview-line-title">{{ bill.billNumber || 'Bill' }}</div>
                        <div class="cell-meta">{{ bill.billDate | date:'yyyy-MM-dd' }}</div>
                    </div>
                    <div class="preview-line-amount">{{ formatMoney(bill.totalAmount, bill.country?.currencyCode) }}</div>
                </div>
            </div>
        </div>
        <ng-template #noVoucherBills>
            <div class="preview-empty">No linked bills.</div>
        </ng-template>
    </p-drawer>
</div>
